<!-- (3)</span> Case studies (including participant examples); -->
<!-- credit="D. Berry, B. Kozlíková, S. O'Donoghue & C. Stolte" -->

<template>
  <p class="intro">
    Register for the VIZBI <i>Online</i> Masterclass for immediate and ongoing access to
    <!-- the videos registration unlocks: their speakers and talks (2026-10-31, Seán) -->
    <RouterLink to="/Videos/Unlock/Masterclass">{{ masterclassVideos }}</RouterLink> of past
    Masterclasses, including this year's. Then join the speakers online on
    <strong>{{ masterclassDay }}</strong>
    <!-- the time zone menu, as /2026's intro (Seán, 2026-11-01) -->
    (<TimeZoneInline suffix="local time" />). They'll recap
    their highlights and share what's new, followed by Q&amp;A, discussion and interactive
    breakout sessions.
  </p>

  <RouterLink to="/2026/Registration" title="Registration fees">
    <HeroImage hero-image="Masterclass.jpg" />
  </RouterLink>
  <RegisterNowWrapper />
  <!-- two paragraphs, each led by its question (Seán, 2026-10-28; the rest in the sidebar's
       FAQ: sidebar.json "audience", "breakouts", "events", "videos", "recorded"), then the FAQ
       and the Masterclass address as buttons, side by side, stacked on a phone -->
  <p>
    <b>Who's it for?</b> The VIZBI <i>Online</i> Masterclass is for anyone working with large,
    complex datasets: scientists, programmers, designers and communicators. You'll learn
    practical techniques from world-leading practitioners in four areas: visual design,
    AI-assisted data visualization, animated data storytelling and Blender programming.
  </p>
  <p>
    <b>How does it work?</b> Watching a few videos beforehand helps you get the most out of it.
    After the speakers' recaps and Q&amp;A, the group splits into three online breakout
    sessions, led by the speakers and covering these four areas: bring your questions and your
    own visualization challenges. The talks are recorded and made available to participants,
    along with a transcript summary of the topics covered in the breakouts.
  </p>
  <div class="further">
    <b>Further questions?</b>
    <RouterLink to="#faq" class="button" @click="scrollToSidebar">FAQ</RouterLink>
    <Email to="masterclass" class="button">Contact Us</Email>
  </div>
  <!--<p><ButtonBespoke :outstanding="true" @click="$router.push('/2023/Registration')">Register now</ButtonBespoke></p>-->
</template>

<script setup>
//import ButtonBespoke from "@/components/ButtonBespoke";
import Email from "@/components/Email";
import HeroImage from "@/components/HeroImage.vue";
import RegisterNowWrapper from "@/components/CallToAction/RegisterNowWrapper";
import useLocalEventDates from "@/composables/useLocalEventDates";
import TimeZoneInline from "@/components/TimeZoneInline.vue";
import useLibraryTotals from "@/composables/useLibraryTotals";
import {computed} from "vue";

const {masterclassDay} = useLocalEventDates(); // in the viewer's time zone
// the Masterclass videos' hours (stats.json, 2026 included; Seán, 2026-10-22, issue V2): none
// shown until it loads
const {stats} = useLibraryTotals();
// "21 videos (28 hours)", or "videos" until stats.json arrives
const masterclassVideos = computed(() => {
  const {videos, hours} = stats.value?.masterclass || {};
  return videos && hours ? `${videos} videos (${hours} hours)` : "videos";
});
// the FAQ button opens the sidebar's panel (its hash); the page scrolled back to the top, where
// the panel is (wide: beside the page; narrow: in its place)
function scrollToSidebar() {
  document.querySelector("#main_content")?.scrollIntoView({behavior: "smooth", block: "start"});
}
</script>

<style scoped>
/* "Further questions?" with the FAQ and Contact Us buttons in one row (Seán, 2026-10-28), the
   buttons outlined in the brand colour as the Videos page's Show All (next steps, not the call
   to action); on a phone (body.narrow) the question on its own line, the buttons below it, one
   above the other, each the full width */
.further {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0 1.5rem;
}
.further b {
  margin-right: 0.25rem;
}
.further .button {
  display: block;
  min-width: 7.5rem;
  padding: 0.4em 1.25em;
  font-weight: 700;
  text-align: center;
  text-decoration: none;
  color: var(--brand-hover-color);
  background-color: var(--background-color);
  border: 1px solid var(--brand-color);
  border-radius: 0.5rem;
  box-shadow: 0 0.13rem 0.13rem rgba(0, 0, 0, 0.2);
}
.further .button:hover {
  color: white;
  background-color: var(--brand-hover-color);
}
.narrow .further {
  flex-direction: column;
  align-items: stretch;
}
.narrow .further b {
  margin: 0;
}
.intro {
  margin-top: 0.35rem;
  margin-bottom: 1rem;
  font-size: 1.05rem;
  line-height: 1.5;
}

span.buttonish {
  color: white;
  background-color: var(--brand-color);
  padding: 2px 4px;
  border-radius: 4px;
}
img {
  width: 100%;
}
p button {
  margin-top: 16px;
  margin-bottom: 16px;
}
h4 {
  margin-bottom: 0rem;
}
:is(p, ul) {
  margin-top: 0.35rem; /* Adjust margin for number alignment */
  margin-bottom: 1rem; /* Adjust margin for number alignment */
}
ul {
  list-style-position: outside; /* Numbers outside the content box */
  margin-left: 1rem; /* Adjust margin for number alignment */
  padding-left: 0; /* No extra padding needed */
}
li {
  text-indent: 0; /* First line aligns naturally with numbers */
  line-height: 1.3;
  margin-bottom: 0.1rem;
}
li strong {
  font-style: italic;
  font-weight: 800;
}
/* not the time zone menu (TimeZoneInline's root span) */
p span:not(.timeZoneInline) {
  font-style: italic; /* Adds subtle emphasis */
  font-weight: 700; /* Slightly bold but not heavy */
  margin-right: 0.25em; /* Adds spacing between the number and text */
}
p span.label {
  all: unset;
  font-style: italic; /* Adds subtle emphasis */
  font-weight: 700; /* Highlight "When" and "Where" */
  margin-right: 0.5em; /* Space after labels */
}
p span.detail {
  all: unset;
}
a span {
  font-weight: normal;
  font-style: normal;
  /* opacity: 0.7; */
}
</style>
